/**
 * Mapping layer between questionnaire fields and the researcher-tools
 * translation contract.
 *
 * Request:  TranslatableField[] + CustomAudienceTranslationGroup[] → TypedQuestion[]
 * Response: TypedQuestion[] × TranslatableText[] → Map<tid, translatedText>
 *
 * DESIGN NOTES
 * ─────────────
 * • Every translatable string is sent as `{ tid, text }`. `tid` is
 *   `fieldIdentity(field)` — the single source of truth for identity.
 *   Correlation on the way back is by `tid` only. Structural IDs are not
 *   sent. `position` is retained as the DeepL ordering key (and may be
 *   sparse — e.g. slider labels at 0 and 2 with no 1).
 *
 * • The request stays a typed tree so the service can translate one survey
 *   document with sibling strings as context. The response is a flat
 *   `[{ tid, text }]` array — one row per requested tid.
 *
 * • Entries are sent with full source text when ANY field is untranslated,
 *   rather than sending only the empty ones. The service translates contextually;
 *   sending fragments degrades quality. Fully translated entries are skipped.
 *
 * • Response validation failures produce no translations at all, so the caller
 *   performs zero writes. This is enforced by accumulating into a local Map and
 *   returning it only after all validation passes.
 *
 * • Single/multi/ranked questions are structurally identical to the service,
 *   so we always emit `single_select` — the service does not care about the
 *   original question type for translation purposes.
 *
 * • Duplicate tids 422 the service; we assert uniqueness locally when building
 *   the request.
 */

import {
  ConjointAttribute,
  MatrixRow,
  MaxDiffLabels,
  ResponseOption,
  ScaleLabel,
  TextHighlighterCategory,
  TranslatableText,
  TypedQuestion
} from '../../../data/rest/researcherTools/types'
import {
  AudienceTranslatableField,
  CustomAudienceTranslationGroup
} from './extractAudienceFields'
import { TranslatableField } from './extractTranslatableFields'

// ─────────────────────────────────────────────────────────────────────────────
// Error
// ─────────────────────────────────────────────────────────────────────────────

export class TranslationPayloadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TranslationPayloadError'
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Identity — derived from entry/child IDs, never from kind names
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives a correlation identity for a field. The identity is built from
 * entry IDs and child IDs, never from field-kind names, so renaming a kind
 * cannot break correlation. Attached on the request as `tid`.
 *
 * Patterns:
 *   entry:<entryId>:text         — question text / highlighter text / conjoint text / title fields
 *   entry:<entryId>:title        — matrix title / text card title / section title
 *   entry:<entryId>:subtitle     — text card subtitle
 *   entry:<entryId>:body         — text card body
 *   entry:<entryId>:content      — highlighter content
 *   entry:<entryId>:option:<id>  — response option
 *   entry:<entryId>:row:<id>     — matrix row
 *   entry:<entryId>:category:<id>— highlighter category
 *   entry:<entryId>:scale:<pos>  — scale label (by position)
 *   entry:<entryId>:maxdiff:most / least / attribute — MaxDiff labels
 *   entry:<entryId>:attribute:<attrId>:name — conjoint attribute name
 *   entry:<entryId>:attribute:<attrId>:option:<optId> — conjoint option
 */
export const fieldIdentity = (
  field: TranslatableField | AudienceTranslatableField,
  audienceMemberId?: string
): string => {
  // Audience fields
  if (field.kind === 'audienceQuestionText') {
    return `entry:${audienceMemberId}:text`
  }
  if (field.kind === 'audienceOptionValue') {
    return `entry:${audienceMemberId}:option:${field.subId}`
  }

  const surveyField = field as TranslatableField

  // Scale labels use position, not ID
  if (surveyField.kind === 'scaleLabel') {
    return `entry:${surveyField.entryId}:scale:${surveyField.scalePosition}`
  }

  // Conjoint attributes and options
  if (surveyField.kind === 'conjointAttributeName' && surveyField.conjoint) {
    return `entry:${surveyField.entryId}:attribute:${surveyField.conjoint.attributeId}:name`
  }
  if (surveyField.kind === 'conjointAttributeOption' && surveyField.conjoint) {
    return `entry:${surveyField.entryId}:attribute:${surveyField.conjoint.attributeId}:option:${surveyField.conjoint.optionId}`
  }

  // Fields with subId — row, option, category
  if (surveyField.subId) {
    if (surveyField.kind === 'matrixRowText') {
      return `entry:${surveyField.entryId}:row:${surveyField.subId}`
    }
    if (surveyField.kind === 'highlighterCategory') {
      return `entry:${surveyField.entryId}:category:${surveyField.subId}`
    }
    // responseOptionValue
    return `entry:${surveyField.entryId}:option:${surveyField.subId}`
  }

  // Slot-based fields (no subId, no nested metadata)
  const slot: Partial<Record<TranslatableField['kind'], string>> = {
    questionText: 'text',
    matrixTitle: 'title',
    textCardTitle: 'title',
    textCardSubtitle: 'subtitle',
    textCardBody: 'body',
    highlighterQuestionText: 'text',
    highlighterContent: 'content',
    maxDiffMostLabel: 'maxdiff:most',
    maxDiffLeastLabel: 'maxdiff:least',
    maxDiffAttributeLabel: 'maxdiff:attribute',
    conjointText: 'text'
  }
  const value = slot[surveyField.kind]
  if (!value) {
    throw new TranslationPayloadError(
      `Unsupported translation field kind: ${surveyField.kind}`
    )
  }
  return `entry:${surveyField.entryId}:${value}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type Field = TranslatableField | AudienceTranslatableField

/**
 * Returns true if ANY field in the group needs translation — i.e. master is
 * non-empty but the current translation is empty.
 */
const needsTranslation = (
  fields: ReadonlyArray<Field>,
  languageCode: string
): boolean =>
  fields.some(
    (field) => field.master.trim() && !field.getCurrent(languageCode).trim()
  )

/**
 * Asserts all values in the array are unique. Throws with actionable context.
 */
const assertUnique = (
  values: ReadonlyArray<string | number>,
  context: string
): void => {
  const seen = new Set<string | number>()
  for (const value of values) {
    if (seen.has(value)) {
      throw new TranslationPayloadError(`Duplicate ${context}: "${value}".`)
    }
    seen.add(value)
  }
}

const asText = (
  field: Field | undefined,
  audienceMemberId?: string
): TranslatableText => ({
  tid: field
    ? fieldIdentity(field, audienceMemberId)
    : `entry:${audienceMemberId ?? 'unknown'}:text`,
  text: field?.master ?? ''
})

const collectTids = (questions: ReadonlyArray<TypedQuestion>): string[] => {
  const tids: string[] = []

  const walk = (question: TypedQuestion): void => {
    switch (question.entry_type) {
      case 'single_select':
      case 'multi_select':
      case 'ranked':
        tids.push(question.question_text.tid)
        for (const option of question.response_options) {
          tids.push(option.value.tid)
        }
        break
      case 'free_text':
        tids.push(question.question_text.tid)
        break
      case 'slider':
        tids.push(question.question_text.tid)
        for (const label of question.scale_labels) {
          tids.push(label.text.tid)
        }
        for (const option of question.response_options) {
          tids.push(option.value.tid)
        }
        break
      case 'max_diff':
        tids.push(question.question_text.tid)
        if (question.labels?.attribute_label) {
          tids.push(question.labels.attribute_label.tid)
        }
        if (question.labels?.most_label) {
          tids.push(question.labels.most_label.tid)
        }
        if (question.labels?.least_label) {
          tids.push(question.labels.least_label.tid)
        }
        for (const option of question.response_options) {
          tids.push(option.value.tid)
        }
        break
      case 'matrix':
        tids.push(question.title.tid)
        for (const row of question.matrix_rows) {
          tids.push(row.text.tid)
        }
        for (const option of question.response_options) {
          tids.push(option.value.tid)
        }
        break
      case 'text_card':
        tids.push(question.title.tid)
        if (question.subtitle != null) {
          tids.push(question.subtitle.tid)
        }
        if (question.body != null) {
          tids.push(question.body.tid)
        }
        break
      case 'section':
        tids.push(question.title.tid)
        for (const nested of question.nested_entries) {
          walk(nested)
        }
        break
      case 'text_highlighter':
        tids.push(question.question_text.tid)
        tids.push(question.content_text.tid)
        for (const category of question.categories) {
          tids.push(category.label.tid)
        }
        break
      case 'conjoint':
        tids.push(question.question_text.tid)
        for (const attribute of question.attributes) {
          tids.push(attribute.name.tid)
          for (const option of attribute.options) {
            tids.push(option.value.tid)
          }
        }
        break
    }
  }

  for (const question of questions) {
    walk(question)
  }
  return tids
}

// ─────────────────────────────────────────────────────────────────────────────
// Packer
// ─────────────────────────────────────────────────────────────────────────────

type SurveyField = TranslatableField

const byKind = (
  fields: SurveyField[],
  kind: SurveyField['kind']
): SurveyField[] => fields.filter((f) => f.kind === kind)

const oneOf = (
  fields: SurveyField[],
  kind: SurveyField['kind']
): SurveyField | undefined => byKind(fields, kind)[0]

/**
 * Maps a group of fields (all sharing the same entryId) into a TypedQuestion.
 */
const mapSurveyGroup = (fields: SurveyField[]): TypedQuestion => {
  const id = fields[0].entryId
  if (!id) {
    throw new TranslationPayloadError(
      'Cannot map fields with undefined entryId.'
    )
  }

  // Build response options (common to several entry types)
  const optionFields = byKind(fields, 'responseOptionValue')
  const responseOptions: ResponseOption[] = optionFields.map((f, idx) => ({
    position: idx,
    value: asText(f)
  }))

  // ─── Matrix ───
  if (oneOf(fields, 'matrixTitle') || byKind(fields, 'matrixRowText').length) {
    const rowFields = byKind(fields, 'matrixRowText')
    const matrixRows: MatrixRow[] = rowFields.map((f, idx) => ({
      position: idx,
      text: asText(f)
    }))
    return {
      entry_type: 'matrix',
      title: asText(oneOf(fields, 'matrixTitle')),
      matrix_rows: matrixRows,
      response_options: responseOptions
    }
  }

  // ─── Text Card ───
  if (
    oneOf(fields, 'textCardTitle') ||
    oneOf(fields, 'textCardSubtitle') ||
    oneOf(fields, 'textCardBody')
  ) {
    return {
      entry_type: 'text_card',
      title: asText(oneOf(fields, 'textCardTitle')),
      subtitle: oneOf(fields, 'textCardSubtitle')
        ? asText(oneOf(fields, 'textCardSubtitle'))
        : null,
      body: oneOf(fields, 'textCardBody')
        ? asText(oneOf(fields, 'textCardBody'))
        : null
    }
  }

  // ─── Text Highlighter ───
  if (
    oneOf(fields, 'highlighterQuestionText') ||
    oneOf(fields, 'highlighterContent')
  ) {
    const categoryFields = byKind(fields, 'highlighterCategory')
    const categories: TextHighlighterCategory[] = categoryFields.map(
      (f, idx) => ({
        position: f.highlighterCategory?.position ?? idx,
        label: asText(f)
      })
    )
    return {
      entry_type: 'text_highlighter',
      question_text: asText(oneOf(fields, 'highlighterQuestionText')),
      content_text: asText(oneOf(fields, 'highlighterContent')),
      categories
    }
  }

  // ─── Conjoint ───
  if (
    oneOf(fields, 'conjointText') ||
    byKind(fields, 'conjointAttributeName').length
  ) {
    const attrNameFields = byKind(fields, 'conjointAttributeName')
    const attrOptionFields = byKind(fields, 'conjointAttributeOption')
    const attributes: ConjointAttribute[] = attrNameFields.map((attrF, idx) => {
      const attrId = attrF.conjoint?.attributeId ?? ''
      const options = attrOptionFields
        .filter((optF) => optF.conjoint?.attributeId === attrId)
        .map((optF, optIdx) => ({
          position: optF.conjoint?.optionPosition ?? optIdx,
          value: asText(optF)
        }))
      return {
        position: attrF.conjoint?.attributePosition ?? idx,
        name: asText(attrF),
        options
      }
    })
    return {
      entry_type: 'conjoint',
      question_text: asText(oneOf(fields, 'conjointText')),
      attributes
    }
  }

  // ─── MaxDiff or Slider or plain question ───
  const hasMaxDiff = !!(
    oneOf(fields, 'maxDiffMostLabel') ||
    oneOf(fields, 'maxDiffLeastLabel') ||
    oneOf(fields, 'maxDiffAttributeLabel')
  )
  const hasScale = byKind(fields, 'scaleLabel').length > 0

  // Guard: MaxDiff and scale are mutually exclusive question types.
  // This is not reachable with valid data (distinct QuestionTypeCode values),
  // but if it happens, fail loudly rather than silently dropping labels.
  if (hasMaxDiff && hasScale) {
    throw new TranslationPayloadError(
      `Entry "${id}" has both MaxDiff labels and scale labels, which are mutually exclusive question types.`
    )
  }

  const questionText = asText(oneOf(fields, 'questionText'))

  // ─── MaxDiff ───
  // ⚠️ MaxDiff extends the options entry: question_text AND response_options
  // are REQUIRED alongside labels.
  if (hasMaxDiff) {
    const labels: MaxDiffLabels = {}
    const mostLabel = oneOf(fields, 'maxDiffMostLabel')
    const leastLabel = oneOf(fields, 'maxDiffLeastLabel')
    const attributeLabel = oneOf(fields, 'maxDiffAttributeLabel')
    if (mostLabel?.master.trim()) labels.most_label = asText(mostLabel)
    if (leastLabel?.master.trim()) labels.least_label = asText(leastLabel)
    if (attributeLabel?.master.trim()) {
      labels.attribute_label = asText(attributeLabel)
    }

    return {
      entry_type: 'max_diff',
      question_text: questionText,
      response_options: responseOptions,
      ...(Object.keys(labels).length > 0 && { labels })
    }
  }

  // ─── Slider ───
  // ⚠️ Slider also requires question_text AND response_options alongside scale_labels.
  if (hasScale) {
    const scaleLabelFields = byKind(fields, 'scaleLabel')
    const scaleLabels: ScaleLabel[] = scaleLabelFields.map((f) => ({
      position: f.scalePosition ?? 0,
      text: asText(f)
    }))
    assertUnique(
      scaleLabels.map((l) => l.position),
      `scale-label position in entry ${id}`
    )
    return {
      entry_type: 'slider',
      question_text: questionText,
      response_options: responseOptions,
      scale_labels: scaleLabels
    }
  }

  // ─── Single Select (covers single/multi/ranked — structurally identical) ───
  if (oneOf(fields, 'questionText') || optionFields.length) {
    return {
      entry_type: 'single_select',
      question_text: questionText,
      response_options: responseOptions
    }
  }

  throw new TranslationPayloadError(
    `Unsupported field collection for entry "${id}".`
  )
}

/**
 * Maps translatable fields and audience groups into TypedQuestion[] for the
 * researcher-tools translation request.
 *
 * Groups fields by entryId and sends an entry's full source text if ANY of
 * its fields are untranslated, preserving contextual translation quality.
 * Fully translated entries are skipped.
 *
 * Guards undefined/blank entryId — skips such fields, consistent with how
 * master's extraction guards missing IDs elsewhere.
 *
 * Pure function — does not mutate inputs.
 */
export const toTranslationRequest = (
  surveyFields: ReadonlyArray<TranslatableField>,
  audienceGroups: ReadonlyArray<CustomAudienceTranslationGroup>,
  languageCode: string
): TypedQuestion[] => {
  // Group survey fields by entryId, skipping undefined/blank IDs
  const groups = new Map<string, SurveyField[]>()
  for (const field of surveyFields) {
    const eid = field.entryId as string | undefined // Guard against runtime undefined
    if (!eid || !eid.trim()) continue // Skip fields with undefined/blank entryId
    const existing = groups.get(eid) ?? []
    groups.set(eid, [...existing, field])
  }

  // Map groups that need translation
  const questions: TypedQuestion[] = []
  for (const fields of groups.values()) {
    if (needsTranslation(fields, languageCode)) {
      questions.push(mapSurveyGroup(fields))
    }
  }

  // Map audience groups
  for (const group of audienceGroups) {
    if (!needsTranslation(group.fields, languageCode)) continue

    const questionField = group.fields.find(
      (f) => f.kind === 'audienceQuestionText'
    )
    const optionFields = group.fields.filter(
      (f) => f.kind === 'audienceOptionValue'
    )
    const responseOptions: ResponseOption[] = optionFields.map((f, idx) => ({
      position: idx,
      value: asText(f, group.memberId)
    }))
    questions.push({
      entry_type: 'single_select',
      question_text: asText(questionField, group.memberId),
      response_options: responseOptions
    })
  }

  assertUnique(collectTids(questions), 'tid across translation payload')

  return questions
}

// ─────────────────────────────────────────────────────────────────────────────
// Response mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps the flat translation service response into a Map<tid, translatedText>.
 *
 * Correlation is by `tid` only. Unexpected, duplicate, or missing tids, or
 * non-string text, fail closed: the local Map is discarded and the caller
 * performs zero writes.
 */
export const fromTranslationResponse = (
  requested: ReadonlyArray<TypedQuestion>,
  response: ReadonlyArray<TranslatableText>
): Map<string, string> => {
  const requestedTids = collectTids(requested)
  const requestedSet = new Set(requestedTids)
  const output = new Map<string, string>()

  for (const row of response) {
    if (typeof row.tid !== 'string' || !row.tid) {
      throw new TranslationPayloadError('Response row is missing a tid.')
    }
    if (!requestedSet.has(row.tid)) {
      throw new TranslationPayloadError(`Unexpected tid "${row.tid}".`)
    }
    if (output.has(row.tid)) {
      throw new TranslationPayloadError(`Duplicate tid "${row.tid}".`)
    }
    if (typeof row.text !== 'string') {
      throw new TranslationPayloadError(`Non-string text for tid "${row.tid}".`)
    }
    output.set(row.tid, row.text)
  }

  for (const tid of requestedTids) {
    if (!output.has(tid)) {
      throw new TranslationPayloadError(`Missing tid "${tid}".`)
    }
  }

  return output
}
